import React from 'react';

import { ConnectedSpaceInfoProvider } from '~src/spacesPlatform/components/Explorer/helpers/SpaceInfoProvider';
import { SpaceInfo, SpaceItemForNonEntitySpace } from '~src/spacesPlatform/state/SpaceInfo';

import { Messages } from '../../../codegen/Localize';
import { BrowserTitleService } from '../../../commonInfra/services/BrowserTitleService';
import { sendTaskDetailsPageViewTelemetry } from '../../telemetry/TaskDetailsPageViewTelemetry';
import { TaskDetailsSpaceContainer } from './TaskDetailsSpaceContainer';

export interface TaskDetailsSpaceProviderProps {
  /** LUID of the task being shown, parsed from the route. */
  readonly taskLuid: string;
  /** Href back to the Jobs page. */
  readonly jobsUrl: string;
  /** Id of the job that navigated here, parsed from the route's `jobId` query param. */
  readonly jobId?: string | null;
}

const TaskDetailsSpaceInfo: SpaceInfo = {
  item: SpaceItemForNonEntitySpace,
  spaceType: 'taskDetails',
  subspaceName: undefined,
};

/**
 * Provider for the Task Details page. Sets the browser title and space info, then
 * renders the container that owns page state.
 */
export const TaskDetailsSpaceProvider: React.FunctionComponent<TaskDetailsSpaceProviderProps> = props => {
  React.useEffect(() => {
    BrowserTitleService.setTitleWithTranslatedSpace({ translatedSpaceName: Messages.taskDetailsBrowserTitle() });
  }, []);

  // PFT page-view emit for PRD success-measure tracking. Keyed on taskLuid (NOT an empty dependency
  // array) because React Router reuses this provider instance when the user navigates from one task's
  // details straight to another's: the route is keyless (see ServerSpaceRouter / SiteSpaceRouter) and
  // TaskDetailsSpaceContainer.componentDidUpdate documents the same reuse. A mount-only effect would
  // emit only for the first task per mounted route and undercount view volume. Fire-and-forget:
  // sendEvent self-swallows failures, so it never disrupts the Task Details render.
  React.useEffect(() => {
    sendTaskDetailsPageViewTelemetry();
  }, [props.taskLuid]);

  return (
    <ConnectedSpaceInfoProvider spaceInfo={TaskDetailsSpaceInfo}>
      <TaskDetailsSpaceContainer taskLuid={props.taskLuid} jobsUrl={props.jobsUrl} jobId={props.jobId} />
    </ConnectedSpaceInfoProvider>
  );
};
